import { useMemo } from 'react'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import dayjs from 'utils/dayjs'
import APIService from 'api/APIService'
import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import { MergeIntegration } from 'types/entity'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'

interface StorageValue {
  integrations: MergeIntegration[]
  mappedIntegrations: Record<string, MergeIntegration>
  expiresAt: string
}

const useMergePublic = () => {
  const {
    permissions: { hasFullPower }
  } = useRolesAndPermissions()

  const [localIntegrations, setLocalIntegrations] =
    useLocalStorage<StorageValue>('group_integrations_metadata', {
      integrations: [],
      mappedIntegrations: {},
      expiresAt: ''
    })

  const hasExpired = useMemo(() => {
    if (localIntegrations.expiresAt === '') return true
    const expiresAt = dayjs(localIntegrations.expiresAt)
    return dayjs().isAfter(expiresAt)
  }, [localIntegrations])

  const { isLoading } = useQueryCallbacks({
    queryKey: ['merge', 'public', 'integrations'],
    queryFn: APIService.integrations.getMergeAvailableIntegrations,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    enabled: hasFullPower && hasExpired,
    onSuccess: (data) => {
      const mappedIntegrations: Record<string, MergeIntegration> = {}
      data.forEach((item: MergeIntegration) => {
        mappedIntegrations[item.slug] = item
      })
      setLocalIntegrations({
        integrations: data,
        mappedIntegrations,
        expiresAt: dayjs().add(2, 'hours').toISOString()
      })
    }
  })

  return {
    integrations: localIntegrations.integrations,
    mappedIntegrations: localIntegrations.mappedIntegrations,
    isLoading
  }
}

export default useMergePublic
